import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getVisitors, updateVisitorStatus } from '@/services/api';
import { Search, CheckCircle2, XCircle, LogIn, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const SecurityVisitors = () => {
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchVisitors = useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const data = await getVisitors();
      setVisitors(data);
      setFilteredVisitors(data);
      setLastUpdated(new Date());
      if (showToast) {
        toast.success('Visitor list refreshed');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to load visitors');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVisitors(false);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchVisitors]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = visitors.filter(
        (v) =>
          v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.gate_pass_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.host_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVisitors(filtered);
    } else {
      setFilteredVisitors(visitors);
    }
  }, [searchTerm, visitors]);

  const handleAction = async (visitorId, status) => {
    try {
      await updateVisitorStatus(visitorId, status);
      toast.success(`Visitor ${status} successfully`);
      fetchVisitors(false); // Refresh after action
    } catch (error) {
      toast.error('Failed to update visitor status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-orange-100 text-orange-700 border-orange-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      checked_in: 'bg-blue-100 text-blue-700 border-blue-200',
      checked_out: 'bg-slate-100 text-slate-700 border-slate-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="security-visitors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Visitor Management</h1>
          <p className="text-sm md:text-base text-slate-600">Approve, check-in, and monitor visitors</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchVisitors(true)}
            disabled={isRefreshing}
            data-testid="refresh-visitors-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search by name, gate pass code, or host..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
              data-testid="visitor-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Visitors List */}
      <div className="grid gap-4">
        {filteredVisitors.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No visitors found</p>
            </CardContent>
          </Card>
        ) : (
          filteredVisitors.map((visitor) => (
            <Card key={visitor.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="visitor-card">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="text-lg md:text-xl font-semibold text-slate-900">{visitor.name}</h3>
                      <Badge className={`${getStatusColor(visitor.status)}`}>
                        {visitor.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 text-sm mb-3">
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Phone</p>
                        <p className="font-medium text-slate-900">{visitor.phone}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Host</p>
                        <p className="font-medium text-slate-900">{visitor.host_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Unit</p>
                        <p className="font-medium text-slate-900">{visitor.host_unit}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Gate Pass</p>
                        <p className="font-bold text-secondary font-mono text-base">{visitor.gate_pass_code}</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-slate-500 text-xs md:text-sm">Purpose: <span className="text-slate-900">{visitor.purpose}</span></p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {visitor.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleAction(visitor.id, 'approved')}
                          data-testid="approve-visitor-btn"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAction(visitor.id, 'rejected')}
                          data-testid="reject-visitor-btn"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                    {visitor.status === 'approved' && (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => handleAction(visitor.id, 'checked_in')}
                        data-testid="checkin-visitor-btn"
                      >
                        <LogIn className="h-4 w-4 mr-1" />
                        Check In
                      </Button>
                    )}
                    {visitor.status === 'checked_in' && (
                      <Button
                        size="sm"
                        className="bg-slate-600 hover:bg-slate-700 text-white"
                        onClick={() => handleAction(visitor.id, 'checked_out')}
                        data-testid="checkout-visitor-btn"
                      >
                        <LogOut className="h-4 w-4 mr-1" />
                        Check Out
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SecurityVisitors;